import { ClassContext } from 'Shared/frontend/styles/ruleProperties';

/*
	The shape of the frontend settings sections, and the only place that has to change as more
	settings move onto the venue document. A branch's 'field' is a step down the document, so a
	section's shape and venue.settings.frontend's shape are the same shape - see SettingsTree.

	Each top-level section is its own export; SettingsPage lists them one per line so their order can
	be rearranged there.
*/

export type SettingsLeaf =
	| {field:string; label:string; kind:'colour'; notes:string}
	| {field:string; label:string; kind:'text';   notes:string}
	| {field:string; label:string; kind:'number'; step:number; notes:string}
	| {field:string; label:string; kind:'fluid';  notes:string}
	| {field:string; label:string; kind:'select'; options:Record<string,string>; notes:string}
	| {field:string; label:string; kind:'map'; itemNoun:string; minEntries:number; notes:string}
	| {field:string; label:string; kind:'time'; notes:string}
	| {field:string; label:string; kind:'styles'; notes:string};

/*
	'light' picks the branch's heading: false is the grey bar with a heavy left rule a top-level node
	gets, true the quieter heading a group of fields inside one gets (the Button / Logo blocks under a
	nav node). See SettingsTree.

	'additive' says the leaves are a catalogue the venue picks from rather than a list it is shown all
	of: only the properties the document carries have a row, and the venue adds and removes them - see
	AdditiveLeaves. Every property of such a branch lands on the one piece of the frontend, so unlike a
	Style Builder rule there is no location to choose alongside them.

	'rules' says the branch's body is a list of style rules instead - the venue picks a part of the
	node and sets properties on it, exactly as it does in the Style Builder - and names the context
	whose parts are on offer. 'none' for a branch that is a list of fields. A rules branch can still
	carry leaves: the nav nodes have a standard-styles picker alongside, which is a list of style tags
	rather than css and belongs in neither the rules nor a part.
*/
export interface SettingsBranch {
	field: string;
	label: string;
	light: boolean;
	additive: boolean;
	rules: ClassContext | 'none';
	notes: string;
	branches: SettingsBranch[];
	leaves: SettingsLeaf[];
}


function colour(field:string,label:string,notes=''): SettingsLeaf
{
	return {field, label, kind:'colour', notes};
}

function text(field:string,label:string,notes=''): SettingsLeaf
{
	return {field, label, kind:'text', notes};
}

function number(field:string,label:string,step:number,notes=''): SettingsLeaf
{
	return {field, label, kind:'number', step, notes};
}

function fluid(field:string,label:string,notes=''): SettingsLeaf
{
	return {field, label, kind:'fluid', notes};
}

function select(field:string,label:string,options:Record<string,string>,notes=''): SettingsLeaf
{
	return {field, label, kind:'select', options, notes};
}

/*
	A {key: label} map the venue edits a row at a time - see SettingLeaf's MapControls. 'itemNoun'
	names one entry ('group' gives an 'Add group' button); 'minEntries' is the fewest rows the map may
	be reduced to (Remove disappears at that point). The key of a new row is derived from its label.
*/
function map(field:string,label:string,itemNoun:string,minEntries:number,notes=''): SettingsLeaf
{
	return {field, label, kind:'map', itemNoun, minEntries, notes};
}

/* A time of day, stored as an 'HH:MM:SS' string (t.time) - the same control the general settings
   use for the day-start / day-end times. */
function time(field:string,label:string,notes=''): SettingsLeaf
{
	return {field, label, kind:'time', notes};
}

/* The standard-styles picker (StylesField) - a list of {category,type,params} tags stored at 'field',
   layered on top of the site-wide styles when the page renders. See SettingLeaf's StylesSetting. */
function styles(field:string,label:string,notes=''): SettingsLeaf
{
	return {field, label, kind:'styles', notes};
}

const textTransformOptions = {
	'':         'Normal',
	uppercase:  'UPPERCASE',
	lowercase:  'lowercase',
	capitalize: 'Capitalise Each Word',
};

const textAlignOptions = {
	'':     'Default',
	left:   'Left',
	center: 'Centre',
	right:  'Right',
};


/*
	One styled piece of frontend text. The leaves line up with venueSettings.textStyle: 'color' names
	a palette entry as colours do everywhere here, and the rest are the plain css values the venue
	settings classes used to hard-code in their text() overrides.

	They are a catalogue rather than a list: a text style records only what the venue wants instead of
	standardText()'s value, so the venue adds the properties it is departing on and removes the ones it
	isn't. A property with no row is absent from the document, and the code default stands.
*/
function textStyleBranch(field:string,label:string,notes:string): SettingsBranch
{
	return {
		field,
		label,
		light: false,
		additive: true,
		rules: 'none',
		notes,
		branches: [],
		leaves: [
			colour('color','Colour'),
			text('fontFamily','Font family',"eg '\"Oswald\", sans-serif'. The font has to be one of the venue's."),
			number('fontWeight','Font weight',100),
			fluid('fontSize','Font size'),
			select('textTransform','Capitalisation',textTransformOptions),
			number('lineHeight','Line height',0.1),
			number('letterSpacing','Letter spacing',0.1,'In pixels'),
			select('textAlign','Alignment',textAlignOptions),
			text('margin','Margin',"css shorthand, eg '.5em 0' or '16px 0'"),
		]
	};
}


/*
	The three nav nodes - the desktop bar, the mobile footer buttons and the mobile 'More' full-screen
	menu. Each is a list of style rules: the venue picks one of that node's parts - its button, a
	hovered button, the logo, the drop-down panel (see settingsParts in ruleProperties.ts) - and sets
	properties on it from the same catalogue the Style Builder offers. standardStyles() carries a
	node's rules as an entry of the 'Settings rules' category and ShowWrapElement lays it over the node's
	wrapper, so a part nothing is said about keeps BaseSettings.nav()'s own value.

	The nodes themselves are spelled out here rather than being parts of one nav, because they are
	three separate pieces of chrome that happen to share a vocabulary - and because 'the button' means
	a different element in each.

	Each also carries a standard-styles picker: initPageStyles() folds
	venue.settings.frontend.nav.{desktop,mobile,mobile.more}.styles in alongside the site-wide styles
	so they reach the nav on every page, and ShowWrapElement resolves them at the same three paths the
	rules land on.
*/

const moreNav: SettingsBranch = {
	field: 'more',
	label: 'More',
	light: false,
	additive: false,
	rules: 'navMore',
	notes: '',
	branches: [],
	leaves: [
		styles('styles','Styles',
			"Standard styles for the mobile 'More' full-screen menu, layered on top of the defaults."),
	]
};

const desktopNav: SettingsBranch = {
	field: 'desktop',
	label: 'Desktop',
	light: false,
	additive: false,
	rules: 'navDesktop',
	notes: '',
	branches: [],
	leaves: [
		styles('styles','Styles',
			'Standard styles for the desktop nav bar, layered on top of the defaults.'),
	]
};

const mobileNav: SettingsBranch = {
	field: 'mobile',
	label: 'Mobile',
	light: false,
	additive: false,
	rules: 'navMobile',
	notes: '',
	branches: [moreNav],
	leaves: [
		styles('styles','Styles',
			'Standard styles for the mobile footer nav buttons, layered on top of the defaults.'),
	]
};


export const navSection: SettingsBranch = {
	field: 'nav',
	label: 'Nav',
	light: false,
	additive: false,
	rules: 'none',
	notes: '',
	branches: [desktopNav,mobileNav],
	leaves: []
};

export const eventSection: SettingsBranch = {
	/* The event page's date and name headings default to the venue 'Page title' text style
	   (BaseSettings.siteTheme()). A venue whose headings depart from it turns on a style through the
	   Styles control below - 'Use Title as Page title' for the venue 'Title' look, a Style Builder
	   style for anything more - or sets properties straight on them with the rule list, whose one
	   part is the date and name headings (venue.settings.frontend.event.rules, resolved onto the
	   <h3>s by the 'Settings rules' category - see standardStyles.ts). */
	field: 'event',
	label: 'Event page',
	light: false,
	additive: false,
	rules: 'event',
	notes: '',
	branches: [],
	leaves: [
		styles('styles','Styles',
			'Standard styles applied to every event page, layered on top of the site-wide styles.'),
		/* The time of day createEvent() pre-fills a new one-off event with (base noon; Family used
		   7pm) - see BaseSettings.backendEvent(). */
		time('defaultStart','Default start time',
			'The start time a newly created one-off event is given until you change it'),
	]
};

/*
	There is no 'Buttons' section any more, no 'Sub Navs' one, and no 'Default styles' block at the
	bottom of Page styles either. A button's look is an ordinary venue style now, built on
	/admin/style-builder out of the same property list every other style is - which is a longer list
	than the eight this file used to name, and one nobody has to add to twice - and which style each
	button takes is a tag in the Site-wide styles picker like any other: 'Buttons > Button style' and
	'Buttons > Sub nav button style', see buttonStyles.ts. A settings row here could only ever say it
	for the whole venue; a tag says it for a page or a single widget too.

	'Sub nav button style' is the button row under a page heading (event lineup filters, menu section
	jumps, a widget's sticky button row) - not shared/frontend/nav/SubNav.tsx, which is the drop-down
	under a desktop nav button and is a part of the Desktop nav node.
*/

export const footerSection: SettingsBranch = {
	/* The divider line above the footer and the venue-name heading. A Styles picker and a rule list,
	   the same pair the nav nodes carry: initPageStyles() folds both venue.settings.frontend.footer's
	   'styles' and 'rules' in alongside the site-wide styles, and ShowWrapElement resolves them at
	   ['footer'] onto the <footer>. The rule list's parts are the bar itself (divider + padding) and
	   the venue-name heading. The colours that used to be set here as a flat object now come from
	   BaseSettings.footer()'s palette-driven default (feature divider, text heading). */
	field: 'footer',
	label: 'Footer',
	light: false,
	additive: false,
	rules: 'footer',
	notes: '',
	branches: [],
	leaves: [
		styles('styles','Styles',
			'Standard styles for the footer, layered on top of the defaults.'),
	]
};

export const textSection: SettingsBranch = {
	field: 'text',
	label: 'Text',
	light: false,
	additive: false,
	rules: 'none',
	notes: '',
	branches: [
		textStyleBranch('pageTitle','Page title',''),
		textStyleBranch('title','Title',''),
		textStyleBranch('teaser','Teaser',''),
		textStyleBranch('tagline','Tagline',''),
		textStyleBranch('subtitle','Subtitle',''),
		textStyleBranch('plain','Plain text',
			"Where body text is actually asked for - a Plain text widget, an event's details, a "+
			"biography, a menu item's details. It no longer sets the page's default: that is the "+
			"'Body font' above, plus a fixed 16px."),
	],
	leaves: [
		text('bodyFontFamily','Body font',
			"eg '\"Oswald\", sans-serif'. The font has to be one of the venue's. The page is drawn "+
			"in this, and so is any text not given a font of its own - a footer address, a menu "+
			"teaser, an event tagline. 'Plain text' and 'Tagline' are built from it too, unless "+
			"they name a font of their own. Leave it empty to stay on the font the venue's code names."),
	]
};

/*
	There is no 'Widgets' section any more. The scrolling row's scrollbar colour and the grid widget's
	cell gaps used to live here (venue.settings.frontend.widgets); both are style tags on the posters /
	grid widget in a venue's page tree now. A scrollbar colour points at a Style Builder style on the
	venue document (venue.settings.frontend.customStyles, seeded by seedWidgetCustomStyles.ts using the
	'Scrollbar colour' property in Shared/frontend/styles/ruleProperties.ts); cell gaps are the
	code-defined 'Layout > Grid gaps' style, carrying the gaps as params on the tag itself.
	Every widget's buttons are styled by the venue's 'Standard Button' style, or - for a sticky button
	row - the one the 'Sub Navs' section names.
*/

export const specialsSection: SettingsBranch = {
	/* The groups a special can belong to - 'General', 'Beers' and the like. The key derived from
	   each label lands on special.type and in a {specials:...} widget's config. A venue starts
	   with {general:'General'} (addVenue.ts) and must keep at least one group - createSpecial
	   uses the first. BaseSettings used to hard-code {general:'General'}; a few venues added a
	   beers group in their siteTheme(). Read back through specialOptions() in
	   shared/backend/widgets/Content.tsx. */
	field: 'specials',
	label: 'Specials',
	light: false,
	additive: false,
	rules: 'none',
	notes: '',
	branches: [],
	leaves: [
		map('groups','Groups','group',1,
			'A special belongs to one of these groups. Removing a group leaves any special already in it pointing at nothing.'),
	]
};

/*
	There is no 'Embedded HTML' section any more. The elements inside a block of embedded /
	user-entered html (a Text widget, an event's Details, a staff resource) are styled entirely in
	BaseSettings.page()'s 'embeddedHtml' block; the only piece a venue ever pulled away from was the
	bold font (The Townie), now an ordinary Style Builder style on the venue document ('Strong text'
	in venue.settings.frontend.customStyles, seeded by seedEmbeddedHtmlStyles.ts) - a 'Font family'
	rule at the 'Bold text' location class, turned on via the venue's site-wide styles.
*/
